import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Card, Input, Space, Tooltip } from "antd"
import { useContext } from "react"
import { useIsRecoveryComponentEnabled } from "../../domain/invoicing/settings/recoveryComponentQueries"
import AddRemove from './AddRemove'
import BudgetCalculatorContext from "./BudgetCalculatorContext"
import CardProcessingOverheadNotice from './CardProcessingOverheadNotice'
import { Dispatch } from "./budgetCalculatorModel"
import BudgetInputNumber from './BudgetInputNumber'
import BudgetStatistic from './BudgetStatistic'
import BudgetTable from './BudgetTable'
import { Overhead, OverheadGroup, isCardProcessingDescription } from "./overheadModel"

export const CARD_PROCESSING_ROW_HINT = 'Pass On Payment Fees is on, so credit card and bank transfer fees are surcharged at checkout. This line only needs to cover payments that aren’t surcharged: debit and prepaid cards, fees above the surcharge cap, and jobs with Pass On Payment Fees turned off.'

const cardProcessingHint = <Tooltip title={CARD_PROCESSING_ROW_HINT}>
  <span role="note" aria-label="Payment fees are surcharged at checkout">
    <FontAwesomeIcon icon={faCircleInfo} />
  </span>
</Tooltip>

const createColumns = (groupName: string, dispatch: Dispatch, showCardProcessingHint: boolean) => {
  return [
    {
      title: 'Expense',
      dataIndex: 'description',
      key: 'description',
      render: (value: string, _: Overhead, index: number) => {
        return <Input
          value={value}
          suffix={showCardProcessingHint && isCardProcessingDescription(value) ? cardProcessingHint : undefined}
          onChange={e => dispatch({ type: 'OVERHEAD_DESCRIPTION_CHANGED', groupName, index, payload: e.target.value })}
        />
      }
    },
    {
      title: 'Monthly Cost',
      key: 'monthlyCost',
      render: (value: number, record: Overhead, index: number) => {
        return <BudgetInputNumber
          value={Math.round((record.annualCost ?? 0) / 12)}
          money
          onChange={(e) => dispatch({ type: 'OVERHEAD_ANNUAL_COST_CHANGED', groupName, index, payload: e * 12 })} />
      }
    },
    {
      title: 'Annual Cost',
      dataIndex: 'annualCost',
      key: 'annualCost',
      render: (value: number, _: Overhead, index: number) => {
        return <BudgetInputNumber
          value={value}
          money
          onChange={(e) => dispatch({ type: 'OVERHEAD_ANNUAL_COST_CHANGED', groupName, index, payload: e })} />
      }
    },
    {
      key: 'operations',
      render: (_: number, _record: Overhead, index: number) => {
        return <AddRemove
          onAdd={() => dispatch({ type: 'ADD_OVERHEAD', groupName, index })}
          onRemove={() => dispatch({ type: 'REMOVE_OVERHEAD', groupName, index })} />
      }
    }
  ]
}

const createTable = (group: OverheadGroup, dispatch: Dispatch, showCardProcessingHint: boolean) => {
  return <BudgetTable
    key={group.groupName}
    columns={createColumns(group.groupName, dispatch, showCardProcessingHint)}
    dataSource={group.overhead}
  />
}

export const OverheadComponent = () => {
  const { worksheet: { data: { overheadGroups } }, summary, statistics, dispatch } = useContext(BudgetCalculatorContext)
  const isRecoveryComponentEnabled = useIsRecoveryComponentEnabled()

  return <Space direction="vertical" size="large" style={{ width: '100%' }}>
    <CardProcessingOverheadNotice overheadGroups={overheadGroups} />
    <Space direction="horizontal" size="large">
      <BudgetStatistic title="Unbillable Labor" value={summary.laborStatistics.unbillableCost} statisticStyle="dollars" />
      <BudgetStatistic title="Equipment Overhead" value={summary.equipmentStatistics.overheadAnnualCost} statisticStyle="dollars" />
      <BudgetStatistic title="Additional Overhead" value={summary.overhead} statisticStyle="dollars" />
      <BudgetStatistic title="Total Overhead" value={statistics.totalOverhead} statisticStyle="dollars" />
    </Space>
    {overheadGroups.map(group => (
      <Card title={group.groupName} key={group.groupName} styles={{ body: { padding: 0 } }}>
        {createTable(group, dispatch, isRecoveryComponentEnabled)}
      </Card>))
    }
  </Space>
}
export default OverheadComponent
